import { useApi, useApiMutation } from './useApi';
import {
  capsulesApi,
  Capsule,
  CreateCapsuleRequest,
  UpdateCapsuleRequest,
} from '@/services/capsulesApi';

export function useCapsules(params?: {
  page?: number;
  limit?: number;
  authorId?: string;
  privacy?: 'public' | 'coauthors' | 'selected';
  isOpened?: boolean;
  isClosed?: boolean;
}) {
  return useApi(
    () => capsulesApi.getCapsules(params),
    [
      params?.page,
      params?.limit,
      params?.authorId,
      params?.privacy,
      params?.isOpened,
      params?.isClosed,
    ]
  );
}

export function useCapsule(id: string) {
  return useApi(async () => {
    const response = await capsulesApi.getCapsule(id);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to load capsule');
  }, [id]);
}

export function useCreateCapsule() {
  return useApiMutation<Capsule, CreateCapsuleRequest>(async data => {
    const response = await capsulesApi.createCapsule(data);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create capsule');
    }
    return response.data;
  });
}

export function useUpdateCapsule() {
  return useApiMutation<Capsule, { id: string; data: UpdateCapsuleRequest }>(
    async ({ id, data }) => {
      const response = await capsulesApi.updateCapsule(id, data);
      return response.data;
    }
  );
}

export function useDeleteCapsule() {
  return useApiMutation<{ message: string }, string>(async id => {
    const response = await capsulesApi.deleteCapsule(id);
    return response.data;
  });
}

export function useOpenCapsule() {
  return useApiMutation<Capsule, string>(async id => {
    const response = await capsulesApi.openCapsule(id);
    return response.data;
  });
}

export function useActivateCapsule() {
  return useApiMutation<Capsule, string | number>(async id => {
    const response = await capsulesApi.activateCapsule(id);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to activate capsule');
  });
}

export function usePosthumousApprove() {
  return useApiMutation<{ success: boolean }, string | number>(async id => {
    const response = await capsulesApi.posthumousApprove(id);
    if (response.success) {
      return { success: true };
    }
    throw new Error(response.message || 'Failed to approve posthumous publish');
  });
}

export function useUpdateAllowedUsers() {
  return useApiMutation<
    Capsule,
    {
      id: string | number;
      allowedUserIds?: number[];
      allowedUserEmails?: string[];
    }
  >(async ({ id, allowedUserIds, allowedUserEmails }) => {
    const response = await capsulesApi.updateAllowedUsers(id, {
      ...(allowedUserIds !== undefined ? { allowedUserIds } : {}),
      ...(allowedUserEmails !== undefined ? { allowedUserEmails } : {}),
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to update allowed users');
  });
}

export function useUpdateCoauthors() {
  return useApiMutation<
    Capsule,
    { id: string | number; coauthorsIds?: number[]; coauthorsEmails?: string[] }
  >(async ({ id, coauthorsIds, coauthorsEmails }) => {
    const response = await capsulesApi.updateCoauthors(id, {
      coauthorsIds,
      coauthorsEmails,
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to update coauthors');
  });
}

export function useRemoveCoauthor() {
  return useApiMutation<{}, { id: string | number; userId: string | number }>(
    async ({ id, userId }) => {
      const response = await capsulesApi.removeCoauthor(id, userId);
      if (response.success) {
        return {};
      }
      throw new Error('Failed to remove coauthor');
    }
  );
}

export function useReinviteCoauthor() {
  return useApiMutation<
    Capsule,
    { id: string | number; userId: string | number }
  >(async ({ id, userId }) => {
    const response = await capsulesApi.reinviteCoauthor(id, userId);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to reinvite coauthor');
  });
}
